import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import type { IntlService } from 'ember-intl';
import type { SafeString } from '@ember/template';
import { formatCurrency } from 'tio-ui/format';
import ProgressBar from 'tio-ui/components/progress-bar';
import { courseCashMark } from 'tio-common/utils/course-cash-mark';
import type {
  DisbursementCard,
  DisbursementCardType,
  DisbursementStatus,
} from 'tio-employee/utils/tas/disbursement';

interface DisbursementSectionSignature {
  Args: {
    cards: DisbursementCard[];
  };
  Element: HTMLDivElement;
}

const TYPE_LABEL_KEYS: Record<DisbursementCardType, string> = {
  reimbursement: 'tuition_assistance.applications.manage.disbursement.reimbursement_label',
  'course-cash': 'tuition_assistance.applications.manage.disbursement.course_cash_label',
  'pre-paid': 'tuition_assistance.applications.manage.disbursement.pre_paid_label',
};

// Only CourseCa$h™ card types get the brand-mark treatment on their type label.
const BRAND_TYPES = new Set<DisbursementCardType>(['course-cash']);

// Requested → warning (approved-but-not-settled/available) → success (active/paid),
// with a dedicated neutral for the CC$-only terminal "deactivated" state — matches
// the dot colors confirmed from the Figma "Disbursement Statuses" table screenshot.
const STATUS_DOT_CLASSES: Record<DisbursementStatus, string> = {
  requested: 'tio-bg-info',
  'pre-approved': 'tio-bg-warning',
  available: 'tio-bg-warning',
  pending: 'tio-bg-warning',
  active: 'tio-bg-success',
  paid: 'tio-bg-success',
  deactivated: 'tio-bg-neutral',
};

const STATUS_LABEL_KEYS: Record<DisbursementStatus, string> = {
  requested: 'tuition_assistance.applications.manage.disbursement.status.requested',
  'pre-approved': 'tuition_assistance.applications.manage.disbursement.status.pre_approved',
  available: 'tuition_assistance.applications.manage.disbursement.status.available',
  // Figma's "Pending" chip shows a dynamic "expected mm/yy" — there's no backing
  // "expected payment date" field yet (same class of gap as attention-tasks.ts's
  // courseCashUseByDate TODO), so this renders the plain status word instead.
  pending: 'tuition_assistance.applications.manage.disbursement.status.pending',
  active: 'tuition_assistance.applications.manage.disbursement.status.active',
  paid: 'tuition_assistance.applications.manage.disbursement.status.paid',
  deactivated: 'tuition_assistance.applications.manage.disbursement.status.deactivated',
};

interface PaymentRow {
  pending: boolean;
  /** "Paid · Jul 28, 2026", or just the status word while pending/undated. */
  label: string;
  amountLabel: string;
}

interface CardRow {
  type: DisbursementCardType;
  status: DisbursementStatus;
  typeLabel: string | SafeString;
  statusLabel: string;
  statusDotClass: string;
  requestedLabel: string;
  approvedLabel?: string;
  useByDateLabel?: string;
  usedLabel?: string;
  remainingLabel?: string;
  usedAmount?: number;
  approvedAmount?: number;
  payments?: PaymentRow[];
}

/**
 * The "Disbursement" rail section of the TAS manage screen — one status card per
 * active payment method (Reimbursement/Pre-Paid, plus CourseCa$h™ when the program
 * has a card-eligible tuition portion), each showing its own Requested/Approved
 * amounts and a colored status dot (Figma: Disbursement Section V2). Renders
 * nothing when there's nothing to disburse.
 */
export default class DisbursementSection extends Component<DisbursementSectionSignature> {
  @service declare intl: IntlService;

  /** `raw` is a date-only "YYYY-MM-DD" value (matches card-details-tab.gts's
   * formattedFundsExpiryDate) — parse the parts as a local date so a
   * UTC-midnight value isn't shifted back a day in the browser's timezone. */
  formatUseByDate = (raw: string | null | undefined): string | undefined => {
    if (!raw) return undefined;
    const [year, month, day] = raw.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return undefined;
    return this.intl.formatDate(new Date(year, month - 1, day), {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  /** Payment dates arrive either date-only ("YYYY-MM-DD" — `reason.payment.date`)
   * or as full ISO datetimes (transition-log timestamps). A date-only value must
   * be parsed as a LOCAL date like formatUseByDate above — `new Date('YYYY-MM-DD')`
   * reads UTC midnight, which renders the previous day west of UTC. */
  formatPaymentDate = (raw: string | null | undefined): string | undefined => {
    if (!raw) return undefined;
    let date: Date;
    if (raw.length === 10) {
      const [year, month, day] = raw.split('-').map(Number);
      if (!year || !month || !day) return undefined;
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(raw);
    }
    if (Number.isNaN(date.getTime())) return undefined;
    return this.intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  get cardRows(): CardRow[] {
    return this.args.cards.map((card) => ({
      type: card.type,
      status: card.status,
      typeLabel: BRAND_TYPES.has(card.type)
        ? courseCashMark(this.intl.t(TYPE_LABEL_KEYS[card.type]))
        : this.intl.t(TYPE_LABEL_KEYS[card.type]),
      statusLabel: this.intl.t(STATUS_LABEL_KEYS[card.status]),
      statusDotClass: STATUS_DOT_CLASSES[card.status],
      requestedLabel: formatCurrency(card.requestedAmount / 100),
      approvedLabel:
        card.approvedAmount != null ? formatCurrency(card.approvedAmount / 100) : undefined,
      useByDateLabel: this.formatUseByDate(card.useByDate),
      usedLabel: card.usedAmount != null ? formatCurrency(card.usedAmount / 100) : undefined,
      remainingLabel:
        card.remainingAmount != null ? formatCurrency(card.remainingAmount / 100) : undefined,
      usedAmount: card.usedAmount,
      approvedAmount: card.approvedAmount,
      payments: card.payments?.map((payment) => {
        const statusLabel = this.intl.t(STATUS_LABEL_KEYS[payment.pending ? 'pending' : 'paid']);
        const dateLabel = this.formatPaymentDate(payment.date);
        return {
          pending: payment.pending,
          label: dateLabel ? `${statusLabel} · ${dateLabel}` : statusLabel,
          amountLabel: formatCurrency(payment.amount / 100),
        };
      }),
    }));
  }

  <template>
    {{#if @cards.length}}
      <div class="tio-col gap-3" ...attributes>
        {{#each this.cardRows as |row|}}
          <div
            class="tio-col gap-2 tio-surface-sunken p-4"
            data-test-disbursement-card={{row.type}}
          >
            <div class="tio-row items-center justify-between gap-2">
              <span class="tio-text-sm-semibold tio-fg-primary">{{row.typeLabel}}</span>
              <span
                class="tio-row items-center gap-1.5"
                data-test-disbursement-status={{row.status}}
              >
                <span class="tio-text-xs-medium tio-fg-secondary">{{row.statusLabel}}</span>
                <span class="h-1.5 w-1.5 rounded-full {{row.statusDotClass}}"></span>
              </span>
            </div>

            <div class="tio-row items-baseline justify-between gap-2">
              <span class="tio-text-sm-normal tio-fg-secondary">
                {{t "tuition_assistance.applications.manage.disbursement.requested_row"}}
              </span>
              <span class="tio-text-sm-semibold tio-fg-primary">{{row.requestedLabel}}</span>
            </div>

            {{#if row.approvedLabel}}
              <div class="tio-row items-baseline justify-between gap-2">
                <span class="tio-text-sm-normal tio-fg-secondary">
                  {{t "tuition_assistance.applications.manage.disbursement.approved_row"}}
                </span>
                <span class="tio-text-sm-semibold tio-fg-primary">{{row.approvedLabel}}</span>
              </div>
            {{/if}}

            {{#if row.useByDateLabel}}
              <div class="tio-row items-baseline justify-between gap-2">
                <span class="tio-text-sm-normal tio-fg-secondary">
                  {{t "tuition_assistance.applications.manage.disbursement.use_by_row"}}
                </span>
                <span class="tio-text-sm-normal tio-fg-primary">{{row.useByDateLabel}}</span>
              </div>
            {{/if}}

            {{#each row.payments as |payment|}}
              <div
                class="tio-row items-baseline justify-between gap-2"
                data-test-disbursement-payment={{if payment.pending "pending" "paid"}}
              >
                <span class="tio-text-sm-normal tio-fg-secondary">{{payment.label}}</span>
                <span class="tio-text-sm-normal tio-fg-primary">{{payment.amountLabel}}</span>
              </div>
            {{/each}}

            {{#if (eq row.status "active")}}
              <ProgressBar
                @value={{row.usedAmount}}
                @max={{row.approvedAmount}}
                @color="teal"
                @size="sm"
                class="mt-1"
              />
              {{! Amount kept as a separate node from its label (not interpolated into
                one sentence) — ember-intl's missing-translation fallback drops
                interpolation options entirely, same gotcha CourseCashBody's
                spent/remaining rows already avoid this way. }}
              <div class="tio-row items-baseline justify-between gap-2">
                <span class="tio-text-sm-normal tio-fg-secondary">
                  {{row.usedLabel}}
                  {{t "tuition_assistance.applications.manage.disbursement.used_suffix"}}
                </span>
                <span class="tio-text-sm-normal tio-fg-secondary">
                  {{row.remainingLabel}}
                  {{t "tuition_assistance.applications.manage.disbursement.remaining_suffix"}}
                </span>
              </div>
            {{else if (eq row.status "deactivated")}}
              <div class="tio-row items-baseline justify-between gap-2">
                <span class="tio-text-sm-normal tio-fg-secondary">
                  {{t "tuition_assistance.applications.manage.disbursement.used_row"}}
                </span>
                <span class="tio-text-sm-semibold tio-fg-primary">{{row.usedLabel}}</span>
              </div>
            {{/if}}
          </div>
        {{/each}}
      </div>
    {{/if}}
  </template>
}
